/**
 * Demo Flow Step Definitions
 *
 * Replaces demoTour.js. Defines the cinematic 8-step demo flow.
 * Step 7 (CHECK_ATTENTION) from the PRD is skipped per implementation decision.
 */

import { isZoomApp } from "@utils/helpers";

export const DEMO_STEPS = {
  INVITE_TO_APP: {
    id: "invite_to_app",
    modalType: "popover",
    heading: "Invite participants to Townhall",
    body: "Share the app with everyone in your meeting so they can join and participate.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    targetSelector: '[data-testid="invite-to-app-button"]',
  },
  SHARE_LINK: {
    id: "share_link",
    modalType: "popover",
    heading: "Share the meeting link",
    body: "First, share this link with your audience so they can join.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    targetSelector: '[data-testid="share-link-button"]',
  },
  COPY_LINK: {
    id: "copy_link",
    modalType: "popover",
    heading: "Copy the join link",
    body: "Click the copy button to grab the link and share it with your audience.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    aboveModal: true,
    targetSelector: '[data-testid="share-modal-copy-join-url-button"]',
  },
  START_MEETING: {
    id: "start_meeting",
    modalType: "popover",
    heading: "Start your meeting",
    body: "Now click 'Start Agenda' to go live and begin tracking engagement.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    targetSelector: '[data-testid="agenda-start-button"]',
  },
  CROWD_ARRIVING: {
    id: "crowd_arriving",
    modalType: "popover",
    heading: "Your audience is joining...",
    body: "Watch as participants arrive. We'll move on once enough people have joined.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    targetSelector: '[data-testid="user-count"]',
  },
  ENGAGEMENT_SIGNAL: {
    id: "engagement_signal",
    modalType: "popover",
    heading: "Your live engagement signal",
    body: "This tracks how engaged your audience is, moment to moment. We'll tuck it away so you can watch your activities — the dot keeps showing the live level.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    targetSelector: '[data-testid="epulse-container"]',
  },
  PLAY_ACTIVITY: {
    id: "play_activity",
    modalType: "popover",
    heading: "Your crowd is here!",
    body: "Click 'Play' to start and watch the responses come in live.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    // targetSelector is DYNAMIC — computed at runtime from first activity ID
    targetSelector: null,
  },
  ACTIVITY_RUNNING: {
    id: "activity_running",
    modalType: "none",
    heading: null,
    body: null,
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
  },
  VIEW_ATTENDEE_DETAIL: {
    id: "view_attendee_detail",
    modalType: "popover",
    heading: "See who responded",
    body: "Click 'Attendee Detail' to see how each person answered.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    // targetSelector is DYNAMIC — computed at runtime from first activity ID
    targetSelector: null,
  },
  READ_ATTENDEE_DETAIL: {
    id: "read_attendee_detail",
    modalType: "none",
    heading: null,
    body: null,
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
  },
  RESULTS_MODAL: {
    id: "results_modal",
    modalType: "results_modal",
    heading: null,
    // body is DYNAMIC — generated at runtime by useDemoFlow from winning option
    body: null,
    buttons: [{ label: "Continue", action: "CONTINUE", variant: "primary" }],
    showCloseButton: false,
    autoDismissMs: null,
  },
  PLAY_SECOND_ACTIVITY: {
    id: "play_second_activity",
    modalType: "popover",
    // heading is DYNAMIC — generated at runtime based on activity type (e.g. "Now let's play a Quiz!")
    heading: "Now let's play the next activity!",
    body: "Click 'Play' to start and watch the responses come in live.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    // targetSelector is DYNAMIC — computed at runtime from second activity ID
    targetSelector: null,
  },
  SECOND_ACTIVITY_RUNNING: {
    id: "second_activity_running",
    modalType: "none",
    heading: null,
    body: null,
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
  },
  VIEW_SECOND_ATTENDEE_DETAIL: {
    id: "view_second_attendee_detail",
    modalType: "popover",
    heading: "See who got it right",
    body: "Click 'Attendee Detail' to see how each person answered.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    // targetSelector is DYNAMIC — computed at runtime from second activity ID
    targetSelector: null,
  },
  READ_SECOND_ATTENDEE_DETAIL: {
    id: "read_second_attendee_detail",
    modalType: "none",
    heading: null,
    body: null,
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
  },
  SECOND_RESULTS_MODAL: {
    id: "second_results_modal",
    modalType: "results_modal",
    heading: null,
    // body is DYNAMIC — generated at runtime by useDemoFlow from winning option
    body: null,
    buttons: [{ label: "Continue", action: "CONTINUE", variant: "primary" }],
    showCloseButton: false,
    autoDismissMs: null,
  },
  PLAY_THIRD_ACTIVITY: {
    id: "play_third_activity",
    modalType: "popover",
    // heading is DYNAMIC — generated at runtime based on activity displayName
    heading: "Now let's share a Link!",
    body: "Click 'Play' to share it with your audience and watch who views it.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    // targetSelector is DYNAMIC — computed at runtime from third activity ID
    targetSelector: null,
  },
  THIRD_ACTIVITY_RUNNING: {
    id: "third_activity_running",
    modalType: "none",
    heading: null,
    body: null,
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
  },
  PLAY_FOURTH_ACTIVITY: {
    id: "play_fourth_activity",
    modalType: "popover",
    // heading is DYNAMIC — generated at runtime based on activity displayName
    heading: "Now let's share an Announcement!",
    body: "Send a one way announcement to boost the mood.",
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    // targetSelector is DYNAMIC — computed at runtime from fourth activity ID
    targetSelector: null,
  },
  FOURTH_ACTIVITY_RUNNING: {
    id: "fourth_activity_running",
    modalType: "none",
    heading: null,
    body: null,
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
  },
  GUIDE_STOP: {
    id: "guide_stop",
    modalType: "popover",
    heading: "End this Agenda to see performance in the E-Report",
    body: null,
    buttons: [],
    showCloseButton: false,
    autoDismissMs: null,
    targetSelector: '[data-testid="agenda-stop-button"]',
  },
  REPORT_CTA: {
    id: "report_cta",
    modalType: "action_card",
    heading: "That Agenda was High Impact!",
    body: "Townhall measures audience engagement via clicks and participation, then tells you what hit and what needs work. Townhall also provides attendance and detailed breakdown of who participated!",
    buttons: [
      {
        label: "View Full Report",
        action: "VIEW_FULL_REPORT",
        variant: "primary",
      },
      {
        label: "Create a Smart Agenda",
        action: "CREATE_SMART_AGENDA",
        variant: "secondary",
      },
    ],
    showCloseButton: false,
    autoDismissMs: null,
  },
};

const SHARED_STEPS = [
  "START_MEETING",
  "CROWD_ARRIVING",
  "ENGAGEMENT_SIGNAL",
  "PLAY_ACTIVITY",
  "ACTIVITY_RUNNING",
  "VIEW_ATTENDEE_DETAIL",
  "READ_ATTENDEE_DETAIL",
  "RESULTS_MODAL",
  "PLAY_SECOND_ACTIVITY",
  "SECOND_ACTIVITY_RUNNING",
  "VIEW_SECOND_ATTENDEE_DETAIL",
  "READ_SECOND_ATTENDEE_DETAIL",
  "SECOND_RESULTS_MODAL",
  "PLAY_THIRD_ACTIVITY",
  "THIRD_ACTIVITY_RUNNING",
  "PLAY_FOURTH_ACTIVITY",
  "FOURTH_ACTIVITY_RUNNING",
  "GUIDE_STOP",
  "REPORT_CTA",
];

export const DEMO_STEP_ORDER = isZoomApp
  ? ["INVITE_TO_APP", ...SHARED_STEPS]
  : ["SHARE_LINK", "COPY_LINK", ...SHARED_STEPS];

export default DEMO_STEPS;
